import { useForm } from "@tanstack/react-form";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/Button";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Input } from "../../components/Input";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { Form } from "../../components/Form";
import { InfoBox } from "../../components/InfoBox";
import type { RestChain } from "@KassaroAB/shared/types/chain";

export type ChainInfoFormValues = {
  name: string;
  brand: string;
  orgNumber: string;
  street: string;
  postalcode: string;
  city: string;
};

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

type ChainInfoFormProps = {
  chain: RestChain;
  onSubmit: (values: ChainInfoFormValues) => void;
  isLoading?: boolean;
  formKey?: string;
  errorMessage?: string;
};

export const ChainInfoForm = ({
  chain,
  onSubmit,
  isLoading,
  formKey,
  errorMessage,
}: ChainInfoFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      name: chain.name,
      brand: chain.brand,
      orgNumber: chain.orgNumber,
      street: chain.street,
      postalcode: chain.postalcode,
      city: chain.city,
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  return (
    <Form
      key={formKey}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {errorMessage ? <InfoBox variant="error">{errorMessage}</InfoBox> : null}

      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("chainSettings.nameRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("chainSettings.name")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="brand"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("chainSettings.brandRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("chainSettings.brand")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="orgNumber"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("chainSettings.orgNumberRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("chainSettings.orgNumber")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="street"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("chainSettings.streetRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("chainSettings.street")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="postalcode"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("chainSettings.postalCodeRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("chainSettings.postalCode")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="city"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("chainSettings.cityRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("chainSettings.city")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <FormActions>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t("common.saving") : t("common.saveChanges")}
        </Button>
      </FormActions>
    </Form>
  );
};
